/**
 * notificationLinks — resolve the in-app route a notification should open.
 *
 * Priority:
 *   1. `data.link` when it is a safe in-app path (starts with "/" but not "//")
 *   2. `data.match_id` → /matches/:id
 *   3. a fallback by notification type, using the ids the backend emit sites
 *      put in `data` (team_id, league_id/leagueId, tournament_id, drop_in_id,
 *      requester_id/recipient_id …)
 *
 * Returns null when there is nowhere sensible to go. `team_invitation` has no
 * dedicated page (invites are answered in the bell's Invites tab), so callers
 * should check `isInviteNotification()` and show that tab instead.
 */

export const isInAppPath = (value) =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

const firstId = (...values) => values.find((v) => v !== undefined && v !== null && v !== "");

/** Notification types whose action lives in the bell's Invites tab. */
export const isInviteNotification = (notif) => notif?.type === "team_invitation";

export function getNotificationLink(notif) {
  if (!notif) return null;
  const data = notif.data || {};
  const type = typeof notif.type === "string" ? notif.type : "";

  if (isInAppPath(data.link)) return data.link;
  if (firstId(data.match_id)) return `/matches/${data.match_id}`;

  // League notifications (incl. team_removed_from_league, which carries leagueId)
  if (type.startsWith("league_") || type === "team_removed_from_league") {
    const id = firstId(data.league_id, data.leagueId);
    return id !== undefined ? `/leagues/${id}` : null;
  }

  if (type.startsWith("tournament_")) {
    const id = firstId(data.tournament_id, data.tournamentId);
    return id !== undefined ? `/tournaments/${id}` : null;
  }

  if (type.startsWith("team_")) {
    if (type === "team_invitation") return null; // handled via the Invites tab
    const id = firstId(data.team_id, data.teamId);
    return id !== undefined ? `/teams/${id}` : null;
  }

  if (type.startsWith("dropin_")) {
    const id = firstId(data.drop_in_id, data.session_id, data.dropin_id);
    return id !== undefined ? `/drop-in/${id}` : null;
  }

  if (type === "friend_request" || type === "friend_request_accepted") {
    // friend_request: { requester_id }; accepted: { recipient_id } (the accepter)
    // or { requester_id } when a mutual request auto-accepted.
    const id = firstId(data.requester_id, data.recipient_id, data.user_id, data.accepter_id);
    return id !== undefined ? `/players/${id}` : null;
  }

  if (type === "role_upgrade_response") return "/profile";

  return null;
}

export default getNotificationLink;
